<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import SuporteAmshiftAPI from 'dashboard/api/suporteAmshift';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import NovoChamado from './NovoChamado.vue';
import MeusChamados from './MeusChamados.vue';
import DetalheDoChamado from './DetalheDoChamado.vue';
import { mensagemDoErro } from './helpers';

const ABAS = ['novo', 'meus'];

const { t } = useI18n();
const { isAdmin } = useAdmin();

const dialog = ref(null);
const aba = ref('novo');
const chamadoAberto = ref(null);
const abrindoCentral = ref(false);

const abas = computed(() => [
  { label: t('SUPORTE_AMSHIFT.ABAS.NOVO') },
  { label: t('SUPORTE_AMSHIFT.ABAS.MEUS') },
]);

const trocarAba = selecionada => {
  aba.value = ABAS[abas.value.findIndex(a => a.label === selecionada.label)];
  chamadoAberto.value = null;
};

const acompanhar = numero => {
  aba.value = 'meus';
  chamadoAberto.value = numero;
};

// A janela abre ainda no clique (senão o bloqueador de pop-up barra) e só
// recebe o endereço quando o link de uso único chega.
const abrirCentral = async () => {
  const janela = window.open('', '_blank');
  abrindoCentral.value = true;
  try {
    const { data } = await SuporteAmshiftAPI.abrirSessao();
    if (!data.linkPortal) throw new Error('sem link');
    if (janela) {
      janela.opener = null;
      janela.location.href = data.linkPortal;
    } else {
      window.open(data.linkPortal, '_blank', 'noopener');
    }
  } catch (e) {
    janela?.close();
    useAlert(
      e.response
        ? mensagemDoErro(e, t('SUPORTE_AMSHIFT.ERRO.GENERICO'))
        : t('SUPORTE_AMSHIFT.CENTRAL_INDISPONIVEL')
    );
  } finally {
    abrindoCentral.value = false;
  }
};

const open = () => {
  aba.value = 'novo';
  chamadoAberto.value = null;
  dialog.value?.open();
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialog"
    width="2xl"
    position="top"
    overflow-y-auto
    :title="t('SUPORTE_AMSHIFT.TITULO')"
    :description="t('SUPORTE_AMSHIFT.DESCRICAO')"
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <div class="flex flex-wrap items-center justify-between gap-3">
      <TabBar
        :key="aba"
        :tabs="abas"
        :initial-active-tab="ABAS.indexOf(aba)"
        @tab-changed="trocarAba"
      />
      <Button
        type="button"
        icon="i-lucide-external-link"
        variant="faded"
        size="sm"
        :label="t('SUPORTE_AMSHIFT.ABRIR_CENTRAL')"
        :is-loading="abrindoCentral"
        :disabled="abrindoCentral"
        @click="abrirCentral"
      />
    </div>

    <NovoChamado v-if="aba === 'novo'" @acompanhar="acompanhar" />
    <DetalheDoChamado
      v-else-if="chamadoAberto"
      :numero="chamadoAberto"
      @voltar="chamadoAberto = null"
    />
    <MeusChamados
      v-else
      :pode-ver-empresa="isAdmin"
      @abrir="chamadoAberto = $event"
    />
  </Dialog>
</template>
